import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { USER_ROLES } from '../config/security'
import ConfirmModal from './ConfirmModal'
import { montarComponentsTemplateMeta, rotuloStatusWhatsApp } from '../utils/whatsappTemplateVars'
import { buscarTemplatePagarFatura, buscarTemplateStatusHomologacao, enviarMensagemWhatsApp } from '../services/whatsappApi'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './FluxoCliente.css'

function etapaPorTipo(etapas, tipo) {
  return (etapas || []).find((e) => e.tipo_etapa === tipo) || null
}

/** Usuários desta empresa veem o filtro por empresa e todos os processos. Demais: só a empresa logada. */
const EMPRESA_ID_PODE_FILTRAR = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

function statusEtapa(status) {
  return status === 'aprovado' ? 'aprovado' : 'pendente'
}

function formatarDataHoraHomolog(value) {
  if (!value) return ''
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function toUi(row) {
  const parecer = etapaPorTipo(row.etapas, 'parecer')
  const vistoria = etapaPorTipo(row.etapas, 'vistoria')
  const carga = etapaPorTipo(row.etapas, 'aumento_carga')
  const cliente = row.cliente || null
  return {
    id: row.id,
    empresa_id: row.empresa_id || null,
    cliente_id: row.cliente_id || cliente?.id || null,
    nome: row.nome,
    uc: row.uc,
    parecer: statusEtapa(parecer?.status),
    protoParecer: parecer?.protocolo || '',
    dataParecer: parecer?.data_etapa || '',
    statusVistoria: statusEtapa(vistoria?.status),
    protoVistoria: vistoria?.protocolo || '',
    dataVistoria: vistoria?.data_etapa || '',
    statusCarga: statusEtapa(carga?.status),
    protoCarga: carga?.protocolo || '',
    dataCarga: carga?.data_etapa || '',
    homologado: row.homologado === true || cliente?.homologado === true,
    dataHomologado: row.data_homologado || cliente?.data_homologado || null,
    telefone: cliente?.telefone || '',
    etapas: row.etapas || []
  }
}

function calcularDiasUteis(dataInicio) {
  if (!dataInicio) return 0
  const atual = new Date(`${dataInicio}T00:00:00`)
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  let diasUteis = 0
  while (atual < hoje) {
    atual.setDate(atual.getDate() + 1)
    const diaSemana = atual.getDay()
    if (diaSemana !== 0 && diaSemana !== 6) diasUteis++
  }
  return diasUteis
}

function calcularDiasCorridos(dataInicio) {
  if (!dataInicio) return 0
  const inicio = new Date(`${dataInicio}T00:00:00`)
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)
  return Math.floor((hoje - inicio) / (1000 * 60 * 60 * 24))
}

function etapaIniciada(status, protocolo, data) {
  return status === 'aprovado' || !!(protocolo && String(protocolo).trim()) || !!data
}

function infoEtapa({ chave, nome, status, data, limite, tipoDias, calcularDias }) {
  const concluido = status === 'aprovado'
  const dias = data ? calcularDias(data) : null
  const atrasado = !concluido && dias != null && dias > limite
  return { chave, nome, dias, limite, tipoDias, atrasado, concluido }
}

function processoConcluido(p) {
  if (p.parecer !== 'aprovado') return false
  const vistoriaIniciada = etapaIniciada(p.statusVistoria, p.protoVistoria, p.dataVistoria)
  const cargaIniciada = etapaIniciada(p.statusCarga, p.protoCarga, p.dataCarga)
  if (!vistoriaIniciada && !cargaIniciada) return false
  if (vistoriaIniciada && p.statusVistoria !== 'aprovado') return false
  if (cargaIniciada && p.statusCarga !== 'aprovado') return false
  return true
}

function etapaAtual(p) {
  const parecer = infoEtapa({
    chave: 'parecer',
    nome: 'Parecer de Acesso',
    status: p.parecer,
    data: p.dataParecer,
    limite: 15,
    tipoDias: 'corridos',
    calcularDias: calcularDiasCorridos
  })
  const vistoria = infoEtapa({
    chave: 'vistoria',
    nome: 'Vistoria',
    status: p.statusVistoria,
    data: p.dataVistoria,
    limite: 5,
    tipoDias: 'úteis',
    calcularDias: calcularDiasUteis
  })
  const carga = infoEtapa({
    chave: 'carga',
    nome: 'Aumento de Carga',
    status: p.statusCarga,
    data: p.dataCarga,
    limite: 5,
    tipoDias: 'úteis',
    calcularDias: calcularDiasUteis
  })

  if (processoConcluido(p)) {
    return { ...parecer, chave: 'concluido', nome: 'Concluído', atrasado: false, concluido: true }
  }

  const todas = [parecer, vistoria, carga]
  const atrasadas = todas.filter((e) => e.atrasado)
  if (atrasadas.length) {
    return atrasadas.reduce((a, b) => ((a.dias || 0) >= (b.dias || 0) ? a : b))
  }

  const incompletas = todas.filter((e) => !e.concluido)
  const iniciadas = incompletas.filter((e) => {
    if (e.chave === 'parecer') return etapaIniciada(p.parecer, p.protoParecer, p.dataParecer)
    if (e.chave === 'vistoria') return etapaIniciada(p.statusVistoria, p.protoVistoria, p.dataVistoria)
    return etapaIniciada(p.statusCarga, p.protoCarga, p.dataCarga)
  })

  if (iniciadas.length === 1) return iniciadas[0]
  if (iniciadas.length > 1) {
    const pos = iniciadas.filter((e) => e.chave !== 'parecer')
    if (pos.length === 2) {
      const comData = pos.filter((e) => e.dias != null)
      return {
        chave: 'pos',
        nome: 'Vistoria / Aumento de Carga',
        dias: comData.length ? Math.max(...comData.map((e) => e.dias)) : null,
        limite: 5,
        tipoDias: 'úteis',
        atrasado: false,
        concluido: false
      }
    }
    return iniciadas[0]
  }

  if (!parecer.concluido) return parecer

  return {
    chave: 'pos',
    nome: 'Vistoria / Aumento de Carga',
    dias: null,
    limite: 5,
    tipoDias: 'úteis',
    atrasado: false,
    concluido: false
  }
}

function montarVarsFluxo(item) {
  const etapa = etapaAtual(item)
  const nome = String(item?.nome || 'Cliente').trim() || 'Cliente'
  let status = 'Em andamento'
  if (item?.homologado) status = 'Homologado'
  else if (etapa.chave === 'concluido' || etapa.concluido) status = 'Aprovado'
  else if (etapa.atrasado) status = 'Atrasado'
  else if (etapa.dias == null) status = 'Aguardando data'
  else status = 'Em análise'

  let prazo = `${etapa.limite} ${etapa.tipoDias === 'úteis' ? 'dias úteis' : 'dias corridos'}`
  if (item?.homologado || etapa.chave === 'concluido') prazo = 'processo concluído'

  const preview = [
    `Status: ${status}`,
    `Olá ${nome},`,
    `informamos que o status do seu processo junto a Equatorial Pará encontra-se na etapa de ${etapa.nome}. Esse processo leva até ${prazo}. Quando houver alguma mudança no seu processo, comunicamos por aqui.`
  ].join('\n')

  return {
    status,
    nome_cliente: nome,
    etapa: etapa.nome,
    prazo,
    preview,
    telefone: item?.telefone || ''
  }
}

const TIPO_AVISO_ETAPA = 'status_etapa'
const TIPO_AVISO_FATURA = 'pagar_fatura'

function etapaEmAumentoDeCarga(p) {
  const etapa = etapaAtual(p)
  if (etapa.chave === 'carga') return true
  return etapa.chave === 'pos'
    && etapaIniciada(p.statusCarga, p.protoCarga, p.dataCarga)
    && p.statusCarga !== 'aprovado'
}

function montarVarsFatura(item) {
  const nome = String(item?.nome || 'Cliente').trim() || 'Cliente'
  const preview = [
    'Hangsix Energia Solar',
    `Olá ${nome} está e uma mensagem automatica da Hangsix Energia Solar para lhe informa que você tem fatura vencida/a vencer na Equatorial Pará diante disso pedimos que faça o pagamento para darmos continuidade no seu processo de homologação junto a concessionarioa para pedir o seu aumento de carga.`,
    '',
    'Lembramos que não pode ter nem uma fatura com status de vencida ou a vencer.',
    'By: Via sistema Conecsolar.'
  ].join('\n')
  return {
    nome_cliente: nome,
    preview,
    telefone: item?.telefone || ''
  }
}

const SELECT_PROCESSOS = `
  id, empresa_id, cliente_id, nome, uc, tipo_fluxo, homologado, data_homologado, created_by, created_at, updated_at,
  etapas:fluxo_cliente_etapas(*),
  cliente:clientes_cadastro(id, homologado, data_homologado, telefone)
`

function normalizarNomeClienteFluxo(s) {
  return String(s || '').trim().toLowerCase()
}

async function buscarClienteIdDoProcesso(p) {
  if (p?.cliente_id) return p.cliente_id
  const nome = String(p?.nome || '').trim()
  if (!nome) return null
  const { data, error } = await supabase
    .from('clientes_cadastro')
    .select('id, nome_completo')
    .ilike('nome_completo', nome)
  if (error) throw error
  const alvo = normalizarNomeClienteFluxo(nome)
  const iguais = (data || []).filter((c) => normalizarNomeClienteFluxo(c.nome_completo) === alvo)
  return iguais.length === 1 ? iguais[0].id : null
}

async function gravarHomologadoEmClientesCadastro(clienteId, homologado, dataHomologado) {
  const { data, error } = await supabase
    .from('clientes_cadastro')
    .update({
      homologado: !!homologado,
      data_homologado: homologado ? (dataHomologado || new Date().toISOString()) : null
    })
    .eq('id', clienteId)
    .select('id, homologado, data_homologado')
    .maybeSingle()
  if (error) throw error
  if (!data) {
    throw new Error('Não foi possível atualizar homologado/data_homologado em clientes_cadastro.')
  }
  return data
}

let correcaoEmpresaProcessosPromise = null

/** Processos antigos: empresa_id / cliente_id passam a ser os do cadastro do cliente, não do usuário que criou. */
async function corrigirEmpresaProcessosExistentes() {
  if (correcaoEmpresaProcessosPromise) return correcaoEmpresaProcessosPromise
  correcaoEmpresaProcessosPromise = (async () => {
    const [{ data: processos, error: errP }, { data: clientes, error: errC }] = await Promise.all([
      supabase.from('fluxo_cliente').select('id, nome, cliente_id, empresa_id'),
      supabase.from('clientes_cadastro').select('id, nome_completo, empresa_id')
    ])
    if (errP) throw errP
    if (errC) throw errC
    if (!processos?.length || !clientes?.length) return

    const porId = new Map((clientes || []).map((c) => [c.id, c]))
    const porNome = new Map()
    for (const c of clientes || []) {
      const n = normalizarNomeClienteFluxo(c.nome_completo)
      if (!n) continue
      if (!porNome.has(n)) porNome.set(n, [])
      porNome.get(n).push(c)
    }

    const updates = []
    for (const p of processos) {
      let cliente = p.cliente_id ? porId.get(p.cliente_id) : null
      if (!cliente) {
        const lista = porNome.get(normalizarNomeClienteFluxo(p.nome)) || []
        if (lista.length === 1) {
          cliente = lista[0]
        } else if (lista.length > 1) {
          const empresasUnicas = [...new Set(lista.map((c) => c.empresa_id).filter(Boolean))]
          if (empresasUnicas.length === 1 && p.empresa_id !== empresasUnicas[0]) {
            updates.push({ id: p.id, empresa_id: empresasUnicas[0], cliente_id: p.cliente_id || null })
          }
          continue
        }
      }
      if (!cliente?.empresa_id) continue
      const clienteId = p.cliente_id || cliente.id
      if (p.cliente_id === clienteId && p.empresa_id === cliente.empresa_id) continue
      updates.push({ id: p.id, cliente_id: clienteId, empresa_id: cliente.empresa_id })
    }

    for (const u of updates) {
      const payload = { empresa_id: u.empresa_id }
      if (u.cliente_id) payload.cliente_id = u.cliente_id
      const { error } = await supabase.from('fluxo_cliente').update(payload).eq('id', u.id)
      if (error) throw error
    }
  })().catch((e) => {
    console.error('Erro ao corrigir empresa dos processos existentes:', e)
  })
  return correcaoEmpresaProcessosPromise
}

function passoTimeline({ key, titulo, status, protocolo, data, limite, tipoDias, calcularDias }) {
  const labelLimite = tipoDias === 'úteis' ? 'dias úteis' : 'dias'
  if (status === 'aprovado') {
    return (
      <div key={key} className="flow-step aprovado-etapa">
        <h3>{titulo} <span className="text-success">(APROVADO)</span></h3>
        <p>Prot: {protocolo || '---'} | Data: {data || '---'}</p>
        <span className="fc-badge fc-badge-approved">Concluído com Sucesso</span>
      </div>
    )
  }
  if (data) {
    const dias = calcularDias(data)
    const atrasado = dias > limite
    return (
      <div key={key} className={`flow-step ${atrasado ? 'atrasado' : ''}`}>
        <h3>{titulo} (Pendente)</h3>
        <p>Prot: {protocolo || '---'} | Data: {data || '---'}</p>
        <span className={`fc-badge ${atrasado ? 'fc-badge-danger' : 'fc-badge-pending'}`}>
          {dias} {labelLimite} decorridos (Limite: {limite} {labelLimite}) {atrasado ? '⚠️ ATRASADO' : ''}
        </span>
      </div>
    )
  }
  return (
    <div key={key} className="flow-step">
      <h3>{titulo}</h3>
      <p>Status: {status} | Prot: {protocolo || '---'} | Data: {data || '---'}</p>
    </div>
  )
}

function TimelineVisual({ p }) {
  if (!p) return null
  return (
    <div className="flow-container">
      {passoTimeline({
        key: 'parecer',
        titulo: 'Parecer de Acesso',
        status: p.parecer,
        protocolo: p.protoParecer,
        data: p.dataParecer,
        limite: 15,
        tipoDias: 'corridos',
        calcularDias: calcularDiasCorridos
      })}
      {passoTimeline({
        key: 'vistoria',
        titulo: 'Solicitar Vistoria',
        status: p.statusVistoria,
        protocolo: p.protoVistoria,
        data: p.dataVistoria,
        limite: 5,
        tipoDias: 'úteis',
        calcularDias: calcularDiasUteis
      })}
      {passoTimeline({
        key: 'carga',
        titulo: 'Aumento de Carga',
        status: p.statusCarga,
        protocolo: p.protoCarga,
        data: p.dataCarga,
        limite: 5,
        tipoDias: 'úteis',
        calcularDias: calcularDiasUteis
      })}
      {p.homologado ? (
        <div className="flow-step aprovado-etapa">
          <h3>Cliente homologado <span className="text-success">(HOMOLOGADO)</span></h3>
          <p>Data: {formatarDataHoraHomolog(p.dataHomologado) || '---'}</p>
          <span className="fc-badge fc-badge-approved">Cliente homologado</span>
        </div>
      ) : (
        <div className="flow-step">
          <h3>Cliente homologado</h3>
          <p>Ainda não marcado como homologado.</p>
          <span className="fc-badge fc-badge-pending">Pendente</span>
        </div>
      )}
    </div>
  )
}

function CamposEtapa({ tituloPrazo, status, onStatus, protocolo, onProtocolo, data, onData }) {
  return (
    <div className="row g-3">
      <div className="col-12 col-md-4">
        <label className="form-label">{tituloPrazo}</label>
        <select className="form-select" value={status} onChange={(e) => onStatus(e.target.value)}>
          <option value="pendente">Pendente</option>
          <option value="aprovado">Aprovado</option>
        </select>
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label">Protocolo:</label>
        <input className="form-control" type="text" value={protocolo} onChange={(e) => onProtocolo(e.target.value)} />
      </div>
      <div className="col-12 col-md-4">
        <label className="form-label">Data:</label>
        <input className="form-control" type="date" value={data} onChange={(e) => onData(e.target.value)} />
      </div>
    </div>
  )
}

export default function FluxoCliente({ onBack, userRole, userEmpresa, userId }) {
  const empresaId = userEmpresa?.id || userEmpresa || null
  const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
  const podeFiltrarEmpresa = String(empresaId || '') === EMPRESA_ID_PODE_FILTRAR

  const [view, setView] = useState('list')
  const [processos, setProcessos] = useState([])
  const [ativo, setAtivo] = useState(null)
  const [nomeCliente, setNomeCliente] = useState('')
  const [ucCliente, setUcCliente] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [modalEtapas, setModalEtapas] = useState(false)
  const [modalCadastro, setModalCadastro] = useState(false)
  const [processoExcluir, setProcessoExcluir] = useState(null)
  const [excluindo, setExcluindo] = useState(false)
  const [filtroBusca, setFiltroBusca] = useState('')
  const [filtroEtapa, setFiltroEtapa] = useState('')
  const [filtroPrazo, setFiltroPrazo] = useState('')
  const [filtroEmpresa, setFiltroEmpresa] = useState('')
  const [filtroHomologado, setFiltroHomologado] = useState('')
  const [empresas, setEmpresas] = useState([])
  const [clientesCadastro, setClientesCadastro] = useState([])
  const [clienteSelecionado, setClienteSelecionado] = useState(null)
  const [showClienteDropdown, setShowClienteDropdown] = useState(false)
  const [enviosPorFluxo, setEnviosPorFluxo] = useState({})
  const [enviosFaturaPorFluxo, setEnviosFaturaPorFluxo] = useState({})
  const [modalWhatsapp, setModalWhatsapp] = useState(null)
  const [enviandoWhatsapp, setEnviandoWhatsapp] = useState(false)
  const saveTimer = useRef(null)

  const nomeEmpresaPorId = (id) => {
    if (!id) return '–'
    if (String(id) === String(empresaId)) return userEmpresa?.empresa || '–'
    const emp = empresas.find((e) => String(e.id) === String(id))
    return emp?.empresa || '–'
  }

  const loadEmpresas = useCallback(async () => {
    if (!podeFiltrarEmpresa) {
      setEmpresas([])
      return
    }
    try {
      const { data, error } = await supabase.from('empresa').select('id, empresa').order('empresa')
      if (error) throw error
      setEmpresas(data || [])
    } catch (e) {
      console.error(e)
      setEmpresas([])
    }
  }, [podeFiltrarEmpresa])

  const loadClientesCadastro = useCallback(async () => {
    try {
      let q = supabase
        .from('clientes_cadastro')
        .select('id, nome_completo, cpf, empresa_id')
        .order('nome_completo')
      if (!podeFiltrarEmpresa && empresaId) q = q.eq('empresa_id', empresaId)
      const { data, error } = await q
      if (error) throw error
      setClientesCadastro(data || [])
    } catch (e) {
      console.error(e)
      setClientesCadastro([])
    }
  }, [empresaId, podeFiltrarEmpresa])

  const loadEnviosWhatsapp = useCallback(async (fluxoIds) => {
    const ids = (fluxoIds || []).filter(Boolean)
    if (!ids.length) {
      setEnviosPorFluxo({})
      setEnviosFaturaPorFluxo({})
      return
    }
    const campos = 'id, fluxo_cliente_id, message_id, status, etapa, prazo, preview, created_at, tipo'
    let { data, error } = await supabase
      .from('homologacao_whatsapp_envios')
      .select(campos)
      .in('fluxo_cliente_id', ids)
      .order('created_at', { ascending: false })
    if (error && String(error.message || '').includes('tipo')) {
      const retry = await supabase
        .from('homologacao_whatsapp_envios')
        .select('id, fluxo_cliente_id, message_id, status, etapa, prazo, preview, created_at')
        .in('fluxo_cliente_id', ids)
        .order('created_at', { ascending: false })
      data = retry.data
      error = retry.error
    }
    if (error) {
      console.error(error)
      return
    }
    const mapa = {}
    const mapaFatura = {}
    for (const row of data || []) {
      if (!row.fluxo_cliente_id) continue
      const tipo = row.tipo || TIPO_AVISO_ETAPA
      if (tipo === TIPO_AVISO_FATURA) {
        if (!mapaFatura[row.fluxo_cliente_id]) mapaFatura[row.fluxo_cliente_id] = row
      } else if (!mapa[row.fluxo_cliente_id]) {
        mapa[row.fluxo_cliente_id] = row
      }
    }
    const comId = [...Object.values(mapa), ...Object.values(mapaFatura)].filter((e) => e.message_id)
    if (comId.length) {
      const { data: msgs } = await supabase
        .from('whatsapp_mensagens')
        .select('message_id, status')
        .in('message_id', comId.map((e) => e.message_id))
      const statusMap = Object.fromEntries((msgs || []).map((m) => [m.message_id, m.status]))
      for (const envio of comId) {
        const st = statusMap[envio.message_id]
        if (st && st !== envio.status) {
          envio.status = st
          await supabase.from('homologacao_whatsapp_envios').update({ status: st }).eq('id', envio.id)
        }
      }
    }
    setEnviosPorFluxo(mapa)
    setEnviosFaturaPorFluxo(mapaFatura)
  }, [])

  const loadProcessos = useCallback(async () => {
    try {
      setLoading(true)
      await corrigirEmpresaProcessosExistentes()
      if (!podeFiltrarEmpresa && !empresaId) {
        setProcessos([])
        return
      }
      let q = supabase.from('fluxo_cliente').select(SELECT_PROCESSOS).order('created_at', { ascending: false })
      if (podeFiltrarEmpresa) {
        if (filtroEmpresa) q = q.eq('empresa_id', filtroEmpresa)
      } else {
        q = q.eq('empresa_id', empresaId)
      }
      const { data, error } = await q
      if (error) throw error
      const lista = (data || []).map(toUi)
      setProcessos(lista)
      await loadEnviosWhatsapp(lista.map((i) => i.id))
    } catch (e) {
      console.error(e)
      setErro('Erro ao carregar processos: ' + e.message)
    } finally {
      setLoading(false)
    }
  }, [empresaId, podeFiltrarEmpresa, filtroEmpresa, loadEnviosWhatsapp])

  useEffect(() => {
    loadProcessos()
  }, [loadProcessos])

  useEffect(() => {
    loadClientesCadastro()
  }, [loadClientesCadastro])

  useEffect(() => {
    if (podeFiltrarEmpresa) loadEmpresas()
    else setFiltroEmpresa('')
  }, [podeFiltrarEmpresa, loadEmpresas])

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    }
  }, [])

  const upsertEtapa = async (processo, tipoEtapa, campos) => {
    const existente = etapaPorTipo(processo.etapas, tipoEtapa)
    const payload = {
      fluxo_cliente_id: processo.id,
      tipo_etapa: tipoEtapa,
      ...campos,
      updated_by: userId || null
    }
    if (existente?.id) payload.id = existente.id
    const { error } = await supabase.from('fluxo_cliente_etapas').upsert(payload, {
      onConflict: 'fluxo_cliente_id,tipo_etapa'
    })
    if (error) throw error
  }

  const persistir = useCallback(async (p) => {
    if (!p?.id) return
    const vistoriaIniciada = etapaIniciada(p.statusVistoria, p.protoVistoria, p.dataVistoria)
    const cargaIniciada = etapaIniciada(p.statusCarga, p.protoCarga, p.dataCarga)
    const tipoDb = cargaIniciada && !vistoriaIniciada ? 'aumento_carga' : 'vistoria'
    const homologado = !!p.homologado
    const dataHomologado = homologado ? (p.dataHomologado || new Date().toISOString()) : null
    const clienteId = await buscarClienteIdDoProcesso(p)
    if (homologado && !clienteId) {
      throw new Error('Não foi possível localizar o cliente em clientes_cadastro para gravar homologado e data_homologado.')
    }

    const payloadFluxo = {
      tipo_fluxo: tipoDb,
      homologado,
      data_homologado: dataHomologado
    }
    if (clienteId && !p.cliente_id) payloadFluxo.cliente_id = clienteId
    const { error } = await supabase.from('fluxo_cliente').update(payloadFluxo).eq('id', p.id)
    if (error) throw error

    if (clienteId) {
      await gravarHomologadoEmClientesCadastro(clienteId, homologado, dataHomologado)
    }

    await upsertEtapa(p, 'parecer', {
      status: p.parecer || 'pendente',
      protocolo: p.protoParecer || null,
      data_etapa: p.dataParecer || null
    })
    await upsertEtapa(p, 'vistoria', {
      status: p.statusVistoria || 'pendente',
      protocolo: p.protoVistoria || null,
      data_etapa: p.dataVistoria || null
    })
    await upsertEtapa(p, 'aumento_carga', {
      status: p.statusCarga || 'pendente',
      protocolo: p.protoCarga || null,
      data_etapa: p.dataCarga || null
    })

    const { data, error: reloadError } = await supabase.from('fluxo_cliente').select(SELECT_PROCESSOS).eq('id', p.id).single()
    if (reloadError) throw reloadError
    const ui = toUi(data)
    setAtivo((current) => {
      if (!current || current.id !== ui.id) return ui
      return {
        ...current,
        etapas: ui.etapas,
        homologado: ui.homologado,
        dataHomologado: ui.dataHomologado,
        cliente_id: ui.cliente_id || clienteId
      }
    })
    setProcessos((lista) => lista.map((item) => (item.id === ui.id ? { ...item, ...ui, ...p, cliente_id: ui.cliente_id || clienteId, etapas: ui.etapas } : item)))
  }, [userId])

  const agendarPersistencia = (p) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      try {
        await persistir(p)
      } catch (e) {
        console.error(e)
        setErro('Erro ao salvar: ' + e.message)
      }
    }, 350)
  }

  const mostrarTela = (id) => {
    setErro('')
    setModalEtapas(false)
    setModalCadastro(false)
    setView(id)
    if (id === 'list') loadProcessos()
  }

  const clientesFiltradosCadastro = useMemo(() => {
    const s = nomeCliente.trim().toLowerCase()
    const lista = clientesCadastro || []
    if (!s) return lista.slice(0, 15)
    return lista.filter((c) => (c.nome_completo || '').toLowerCase().includes(s)).slice(0, 15)
  }, [clientesCadastro, nomeCliente])

  const abrirCadastro = () => {
    setNomeCliente('')
    setUcCliente('')
    setClienteSelecionado(null)
    setShowClienteDropdown(false)
    setErro('')
    setModalCadastro(true)
  }

  const selecionarClienteCadastro = (cliente) => {
    setClienteSelecionado(cliente)
    setNomeCliente(cliente.nome_completo || '')
    setShowClienteDropdown(false)
    setErro('')
  }

  const salvarProcesso = async (e) => {
    e.preventDefault()
    const nome = nomeCliente.trim()
    const uc = ucCliente.trim()
    if (!nome || !uc) {
      setErro('Informe o nome e a UC.')
      return
    }

    let cliente = clienteSelecionado
    if (!cliente?.id || (cliente.nome_completo || '').trim().toLowerCase() !== nome.toLowerCase()) {
      const iguais = clientesCadastro.filter(
        (c) => (c.nome_completo || '').trim().toLowerCase() === nome.toLowerCase()
      )
      cliente = iguais.length === 1 ? iguais[0] : null
    }
    if (!cliente?.id) {
      setErro('Selecione um cliente cadastrado na lista. O processo fica na empresa do cliente, não na do usuário.')
      setShowClienteDropdown(true)
      return
    }
    if (!cliente.empresa_id) {
      setErro('Este cliente não possui empresa cadastrada. Atualize o cadastro do cliente antes de criar o processo.')
      return
    }

    try {
      setSaving(true)
      setErro('')
      const { data: processo, error } = await supabase
        .from('fluxo_cliente')
        .insert([{
          nome: cliente.nome_completo.trim(),
          uc,
          tipo_fluxo: 'vistoria',
          empresa_id: cliente.empresa_id,
          cliente_id: cliente.id,
          created_by: userId || null
        }])
        .select()
        .single()
      if (error) throw error

      const { error: etapaError } = await supabase.from('fluxo_cliente_etapas').insert([{
        fluxo_cliente_id: processo.id,
        tipo_etapa: 'parecer',
        status: 'pendente',
        updated_by: userId || null
      }])
      if (etapaError) throw etapaError

      setNomeCliente('')
      setUcCliente('')
      setClienteSelecionado(null)
      setModalCadastro(false)
      await abrirProcesso(processo.id)
      setModalEtapas(true)
    } catch (err) {
      console.error(err)
      setErro('Erro ao salvar: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const abrirProcesso = async (id) => {
    try {
      setErro('')
      const { data, error } = await supabase.from('fluxo_cliente').select(SELECT_PROCESSOS).eq('id', id).single()
      if (error) throw error
      setAtivo(toUi(data))
      setModalCadastro(false)
      setView('detalhes')
      setModalEtapas(false)
    } catch (err) {
      console.error(err)
      setErro('Erro ao abrir processo: ' + err.message)
    }
  }

  const atualizarCampo = (campo, valor) => {
    setAtivo((prev) => {
      if (!prev) return prev
      const next = { ...prev, [campo]: valor }
      agendarPersistencia(next)
      return next
    })
  }

  const atualizarHomologado = (marcado) => {
    setAtivo((prev) => {
      if (!prev) return prev
      const next = {
        ...prev,
        homologado: marcado,
        dataHomologado: marcado ? new Date().toISOString() : null
      }
      if (saveTimer.current) clearTimeout(saveTimer.current)
      persistir(next).catch((e) => {
        console.error(e)
        setErro('Erro ao salvar: ' + e.message)
      })
      return next
    })
  }

  const excluirProcesso = async () => {
    if (!processoExcluir || !isAdmin) return
    try {
      setExcluindo(true)
      setErro('')
      const { error } = await supabase.from('fluxo_cliente').delete().eq('id', processoExcluir.id)
      if (error) throw error
      if (ativo?.id === processoExcluir.id) {
        setAtivo(null)
        setModalEtapas(false)
        setView('list')
      }
      setProcessoExcluir(null)
      await loadProcessos()
    } catch (err) {
      console.error(err)
      setErro('Erro ao excluir processo: ' + err.message)
      setProcessoExcluir(null)
    } finally {
      setExcluindo(false)
    }
  }

  const processosFiltrados = useMemo(() => {
    const busca = filtroBusca.trim().toLowerCase()
    return processos.filter((item) => {
      const etapa = etapaAtual(item)
      if (busca) {
        const nome = (item.nome || '').toLowerCase()
        const uc = (item.uc || '').toLowerCase()
        if (!nome.includes(busca) && !uc.includes(busca)) return false
      }
      if (filtroEtapa === 'parecer' && item.parecer === 'aprovado') return false
      if (filtroEtapa === 'vistoria' && item.statusVistoria === 'aprovado') return false
      if (filtroEtapa === 'carga' && item.statusCarga === 'aprovado') return false
      if (filtroPrazo === 'atrasado' && !etapa.atrasado) return false
      if (filtroPrazo === 'no_prazo' && (etapa.atrasado || etapa.concluido || etapa.dias == null)) return false
      if (filtroPrazo === 'aguardando' && etapa.dias != null) return false
      if (filtroPrazo === 'concluido' && !etapa.concluido) return false
      if (filtroHomologado === 'sim' && !item.homologado) return false
      if (filtroHomologado === 'nao' && item.homologado) return false
      return true
    })
  }, [processos, filtroBusca, filtroEtapa, filtroPrazo, filtroHomologado])

  const limparFiltros = () => {
    setFiltroBusca('')
    setFiltroEtapa('')
    setFiltroPrazo('')
    setFiltroEmpresa('')
    setFiltroHomologado('')
  }

  const abrirModalWhatsapp = (item, tipo = TIPO_AVISO_ETAPA) => {
    const avisoFatura = tipo === TIPO_AVISO_FATURA
    if (avisoFatura && !etapaEmAumentoDeCarga(item)) return
    const vars = avisoFatura ? montarVarsFatura(item) : montarVarsFluxo(item)
    const digits = String(vars.telefone || '').replace(/\D/g, '')
    setErro(digits.length < 10
      ? 'Este cliente não tem telefone cadastrado. Inclua o número em Cadastrar Clientes para enviar o aviso no WhatsApp.'
      : '')
    setModalWhatsapp({
      tipo,
      item,
      vars,
      envio: avisoFatura ? (enviosFaturaPorFluxo[item.id] || null) : (enviosPorFluxo[item.id] || null)
    })
  }

  const sincronizarEnvioModal = useCallback(async (envio) => {
    if (!envio?.id) return envio
    let atual = envio
    if (envio.message_id) {
      const { data } = await supabase
        .from('whatsapp_mensagens')
        .select('status')
        .eq('message_id', envio.message_id)
        .maybeSingle()
      if (data?.status && data.status !== envio.status) {
        atual = { ...envio, status: data.status }
        await supabase.from('homologacao_whatsapp_envios').update({ status: data.status }).eq('id', envio.id)
      }
    }
    if (envio.fluxo_cliente_id) {
      if ((envio.tipo || TIPO_AVISO_ETAPA) === TIPO_AVISO_FATURA) {
        setEnviosFaturaPorFluxo((prev) => ({ ...prev, [envio.fluxo_cliente_id]: atual }))
      } else {
        setEnviosPorFluxo((prev) => ({ ...prev, [envio.fluxo_cliente_id]: atual }))
      }
    }
    setModalWhatsapp((prev) => (prev ? { ...prev, envio: atual } : prev))
    return atual
  }, [])

  useEffect(() => {
    const envio = modalWhatsapp?.envio
    if (!envio?.message_id) return undefined
    sincronizarEnvioModal(envio)
    const t = setInterval(() => sincronizarEnvioModal(envio), 4000)
    return () => clearInterval(t)
  }, [modalWhatsapp?.envio?.id, modalWhatsapp?.envio?.message_id, sincronizarEnvioModal])

  const enviarAvisoHomologacao = async () => {
    if (!modalWhatsapp?.item || !userId) return
    const avisoFatura = modalWhatsapp.tipo === TIPO_AVISO_FATURA
    if (avisoFatura && !etapaEmAumentoDeCarga(modalWhatsapp.item)) {
      setErro('O aviso de fatura só pode ser enviado na etapa de Aumento de Carga.')
      return
    }
    const vars = modalWhatsapp.vars
    const digits = String(vars.telefone || '').replace(/\D/g, '')
    if (digits.length < 10) {
      setErro('Este cliente não tem telefone válido no cadastro.')
      return
    }
    setEnviandoWhatsapp(true)
    setErro('')
    try {
      const tpl = avisoFatura
        ? await buscarTemplatePagarFatura({ usuario_id: userId })
        : await buscarTemplateStatusHomologacao({ usuario_id: userId })
      if (!tpl.success) {
        setErro(tpl.error || (avisoFatura
          ? 'Template template_pagar_fatura não encontrado.'
          : 'Template template_status_homologacao não encontrado.'))
        return
      }
      const templateVars = avisoFatura
        ? { nome_cliente: vars.nome_cliente }
        : {
            'Order Status': vars.status,
            order_status: vars.status,
            status: vars.status,
            nome_cliente: vars.nome_cliente,
            etapa: vars.etapa,
            prazo: vars.prazo
          }
      const components = montarComponentsTemplateMeta(
        tpl.template?.components || [],
        templateVars,
        avisoFatura
          ? [vars.nome_cliente]
          : [vars.status, vars.nome_cliente, vars.etapa, vars.prazo]
      )
      const templateName = tpl.template_name || (avisoFatura ? 'template_pagar_fatura' : 'template_status_homologacao')
      const res = await enviarMensagemWhatsApp({
        usuario_id: userId,
        to: digits,
        type: 'template',
        template_id: tpl.template_id,
        template_name: templateName,
        template_language: tpl.template_language || 'pt_BR',
        template_components: components.length ? components : undefined,
        template_vars: templateVars,
        origem: 'homologacao',
        contato_nome: vars.nome_cliente,
        body_preview: vars.preview,
        vendedor_id: userId
      })
      if (!res.success) {
        setErro(res.error || 'Não foi possível enviar o aviso.')
        return
      }
      const tipo = avisoFatura ? TIPO_AVISO_FATURA : TIPO_AVISO_ETAPA
      const { data: gravado, error: gravarError } = await supabase
        .from('homologacao_whatsapp_envios')
        .insert({
          fluxo_cliente_id: modalWhatsapp.item.id,
          cliente_id: modalWhatsapp.item.cliente_id || null,
          usuario_id: userId,
          message_id: res.message_id || null,
          conversa_id: res.conversa_id || null,
          status: 'sent',
          tipo,
          etapa: avisoFatura ? 'Aumento de Carga' : vars.etapa,
          prazo: avisoFatura ? null : vars.prazo,
          preview: vars.preview
        })
        .select('id, fluxo_cliente_id, message_id, status, etapa, prazo, preview, created_at, tipo')
        .single()
      if (gravarError) {
        console.error(gravarError)
        const precisaTipo = String(gravarError.message || '').includes('tipo')
        setErro(precisaTipo
          ? 'Aviso enviado, mas o histórico não pôde ser gravado. Execute o SQL 160_homologacao_whatsapp_tipo_fatura.sql.'
          : 'Aviso enviado, mas o histórico não pôde ser gravado. Execute o SQL 159_homologacao_whatsapp_fluxo_cliente.sql.')
      }
      const envio = gravado || {
        fluxo_cliente_id: modalWhatsapp.item.id,
        message_id: res.message_id || null,
        status: 'sent',
        tipo
      }
      if (avisoFatura) {
        setEnviosFaturaPorFluxo((prev) => ({ ...prev, [modalWhatsapp.item.id]: envio }))
      } else {
        setEnviosPorFluxo((prev) => ({ ...prev, [modalWhatsapp.item.id]: envio }))
      }
      setModalWhatsapp((prev) => ({ ...prev, envio }))
    } finally {
      setEnviandoWhatsapp(false)
    }
  }

  const p = ativo

  return (
    <div className="fc-app">
      <div className="container-fluid px-3 px-md-4">
        <header className="mb-3">
          {view === 'list' && (
            <button type="button" className="fc-link-voltar d-block" onClick={onBack}>← Voltar</button>
          )}
          <h1 className="h3 mb-0">Painel de Homologações</h1>
        </header>

        {view === 'list' && (
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <h2 className="h5 mb-0">Processos</h2>
                <button
                  type="button"
                  className="btn btn-primary btn-sm flex-shrink-0 btn-novo-processo"
                  onClick={abrirCadastro}
                >
                  + Novo Processo
                </button>
              </div>
              {erro && !modalCadastro && <div className="alert alert-danger py-2">{erro}</div>}
              <div className="row g-2 mb-3">
                {podeFiltrarEmpresa && (
                  <div className="col-12 col-md-3">
                    <label className="form-label mb-1" htmlFor="fc-filtro-empresa">Empresa</label>
                    <select
                      id="fc-filtro-empresa"
                      className="form-select"
                      value={filtroEmpresa}
                      onChange={(e) => setFiltroEmpresa(e.target.value)}
                    >
                      <option value="">Todas as empresas</option>
                      {empresas.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.empresa}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={podeFiltrarEmpresa ? 'col-12 col-md-3' : 'col-12 col-md-5'}>
                  <label className="form-label mb-1" htmlFor="fc-filtro-busca">Cliente ou UC</label>
                  <input
                    id="fc-filtro-busca"
                    type="text"
                    className="form-control"
                    placeholder="Buscar por nome ou UC"
                    value={filtroBusca}
                    onChange={(e) => setFiltroBusca(e.target.value)}
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-2">
                  <label className="form-label mb-1" htmlFor="fc-filtro-etapa">Etapa</label>
                  <select
                    id="fc-filtro-etapa"
                    className="form-select"
                    value={filtroEtapa}
                    onChange={(e) => setFiltroEtapa(e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="parecer">Parecer de Acesso</option>
                    <option value="vistoria">Vistoria</option>
                    <option value="carga">Aumento de Carga</option>
                  </select>
                </div>
                <div className="col-12 col-sm-6 col-md-2">
                  <label className="form-label mb-1" htmlFor="fc-filtro-prazo">Prazo</label>
                  <select
                    id="fc-filtro-prazo"
                    className="form-select"
                    value={filtroPrazo}
                    onChange={(e) => setFiltroPrazo(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="atrasado">Atrasado</option>
                    <option value="no_prazo">No prazo</option>
                    <option value="aguardando">Aguardando data</option>
                    <option value="concluido">Concluído</option>
                  </select>
                </div>
                <div className="col-12 col-sm-6 col-md-2">
                  <label className="form-label mb-1" htmlFor="fc-filtro-homologado">Homologado</label>
                  <select
                    id="fc-filtro-homologado"
                    className="form-select"
                    value={filtroHomologado}
                    onChange={(e) => setFiltroHomologado(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="sim">Homologados</option>
                    <option value="nao">Não homologados</option>
                  </select>
                </div>
                <div className="col-12 col-md-auto d-flex align-items-end">
                  <button type="button" className="btn btn-outline-secondary btn-sm flex-shrink-0" onClick={limparFiltros} title="Limpar filtros">
                    Limpar
                  </button>
                </div>
              </div>
              {loading && <p className="text-muted mb-0">Carregando...</p>}
              {!loading && processos.length === 0 && (
                <p className="text-muted mb-0">Nenhum processo cadastrado.</p>
              )}
              {!loading && processos.length > 0 && processosFiltrados.length === 0 && (
                <p className="text-muted mb-0">Nenhum processo encontrado com os filtros.</p>
              )}
              {!loading && processosFiltrados.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>UC</th>
                        {podeFiltrarEmpresa && <th>Empresa</th>}
                        <th>Etapa</th>
                        <th>Homologado</th>
                        <th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processosFiltrados.map((item) => {
                        const etapa = etapaAtual(item)
                        return (
                          <tr key={item.id}>
                            <td>{item.nome}</td>
                            <td>{item.uc}</td>
                            {podeFiltrarEmpresa && <td>{nomeEmpresaPorId(item.empresa_id)}</td>}
                            <td>
                              <div className="fw-semibold">{etapa.nome}</div>
                              <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                {etapa.concluido ? (
                                  <span className="fc-badge fc-badge-approved">No prazo · Concluído</span>
                                ) : etapa.dias == null ? (
                                  <span className="fc-badge fc-badge-pending">Aguardando data</span>
                                ) : (
                                  <>
                                    <span className={`fc-etapa-dias-valor ${etapa.atrasado ? 'fc-etapa-dias-atrasado' : 'fc-etapa-dias-ok'}`}>
                                      {etapa.dias} {etapa.tipoDias === 'úteis' ? 'dias úteis' : 'dias corridos'}
                                      <span className="fc-etapa-limite"> / {etapa.limite}</span>
                                    </span>
                                    <span className={`fc-badge ${etapa.atrasado ? 'fc-badge-danger' : 'fc-badge-approved'}`}>
                                      {etapa.atrasado ? 'Atrasado' : 'No prazo'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </td>
                            <td>
                              {item.homologado ? (
                                <span className="fc-badge fc-badge-approved">Homologado</span>
                              ) : (
                                <span className="fc-badge fc-badge-pending">Não</span>
                              )}
                            </td>
                            <td className="text-end fc-acoes">
                              <div className="d-inline-flex gap-1 justify-content-end align-items-center">
                                {(() => {
                                  const envio = enviosPorFluxo[item.id]
                                  const st = rotuloStatusWhatsApp(envio?.status)
                                  return (
                                    <button
                                      type="button"
                                      className={`btn btn-sm fc-btn-whatsapp fc-btn-whatsapp--${st.classe}`}
                                      title={`WhatsApp: ${st.label}`}
                                      onClick={() => abrirModalWhatsapp(item)}
                                    >
                                      <i className="bi bi-chat-dots-fill" />
                                      <span>{st.label}</span>
                                    </button>
                                  )
                                })()}
                                {etapaEmAumentoDeCarga(item) && (() => {
                                  const envioFat = enviosFaturaPorFluxo[item.id]
                                  const stFat = rotuloStatusWhatsApp(envioFat?.status)
                                  return (
                                    <button
                                      type="button"
                                      className={`btn btn-sm fc-btn-whatsapp fc-btn-fatura fc-btn-whatsapp--${stFat.classe}`}
                                      title={`Aviso de fatura: ${stFat.label}`}
                                      onClick={() => abrirModalWhatsapp(item, TIPO_AVISO_FATURA)}
                                    >
                                      <i className="bi bi-receipt" />
                                      <span>Fatura</span>
                                    </button>
                                  )
                                })()}
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => abrirProcesso(item.id)}>
                                  Acompanhar
                                </button>
                                {isAdmin && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => setProcessoExcluir(item)}
                                    title="Excluir processo"
                                  >
                                    Excluir
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'detalhes' && p && (
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3 mb-3">
                <div>
                  <button type="button" className="btn btn-secondary btn-sm mb-2" onClick={() => mostrarTela('list')}>
                    ← Voltar
                  </button>
                  <h2 className="h4 mb-1">{p.nome}</h2>
                  <p className="text-muted mb-1">UC {p.uc}</p>
                  {p.homologado ? (
                    <span className="fc-badge fc-badge-approved">Cliente homologado</span>
                  ) : (
                    <span className="fc-badge fc-badge-pending">Não homologado</span>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`btn fc-btn-whatsapp fc-btn-whatsapp--${rotuloStatusWhatsApp(enviosPorFluxo[p.id]?.status).classe}`}
                    onClick={() => abrirModalWhatsapp(p)}
                  >
                    <i className="bi bi-chat-dots-fill" />
                    <span>{rotuloStatusWhatsApp(enviosPorFluxo[p.id]?.status).label}</span>
                  </button>
                  {etapaEmAumentoDeCarga(p) && (
                    <button
                      type="button"
                      className={`btn fc-btn-whatsapp fc-btn-fatura fc-btn-whatsapp--${rotuloStatusWhatsApp(enviosFaturaPorFluxo[p.id]?.status).classe}`}
                      onClick={() => abrirModalWhatsapp(p, TIPO_AVISO_FATURA)}
                    >
                      <i className="bi bi-receipt" />
                      <span>Fatura</span>
                    </button>
                  )}
                                <button type="button" className="btn btn-primary" onClick={() => setModalEtapas(true)}>
                    Etapas
                  </button>
                  {isAdmin && (
                    <button type="button" className="btn btn-outline-danger" onClick={() => setProcessoExcluir(p)}>
                      Excluir processo
                    </button>
                  )}
                </div>
              </div>
              {erro && <div className="alert alert-danger py-2">{erro}</div>}
              <TimelineVisual p={p} />
            </div>
          </div>
        )}
      </div>

      {modalCadastro && (
        <div
          className="modal fade show fc-modal-etapas"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fc-modal-cadastro-titulo"
          onClick={() => { setModalCadastro(false); setErro('') }}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={salvarProcesso}>
                <div className="modal-header">
                  <h5 className="modal-title" id="fc-modal-cadastro-titulo">Cadastrar Nova Homologação</h5>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={() => { setModalCadastro(false); setErro('') }} />
                </div>
                <div className="modal-body">
                  {erro && <div className="alert alert-danger py-2">{erro}</div>}
                  <div className="mb-3">
                    <label className="form-label" htmlFor="fc-nome">Cliente cadastrado:</label>
                    <div className="fc-cliente-busca">
                      <input
                        id="fc-nome"
                        className="form-control"
                        type="text"
                        value={nomeCliente}
                        onChange={(e) => {
                          setNomeCliente(e.target.value)
                          setClienteSelecionado(null)
                          setShowClienteDropdown(true)
                        }}
                        onFocus={() => setShowClienteDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClienteDropdown(false), 180)}
                        placeholder="Buscar pelo nome do cliente"
                        autoComplete="off"
                        required
                      />
                      {showClienteDropdown && clientesFiltradosCadastro.length > 0 && (
                        <div className="fc-cliente-dropdown">
                          {clientesFiltradosCadastro.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="fc-cliente-dropdown-item"
                              onClick={() => selecionarClienteCadastro(c)}
                            >
                              <span className="fw-semibold d-block">{c.nome_completo}</span>
                              {podeFiltrarEmpresa && (
                                <span className="text-muted small">{nomeEmpresaPorId(c.empresa_id)}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {clienteSelecionado && (
                      <small className="text-muted d-block mt-1">
                        Empresa do cliente: {nomeEmpresaPorId(clienteSelecionado.empresa_id)}
                      </small>
                    )}
                  </div>
                  <div className="mb-0">
                    <label className="form-label" htmlFor="fc-uc">UC:</label>
                    <input
                      id="fc-uc"
                      className="form-control"
                      type="text"
                      value={ucCliente}
                      onChange={(e) => setUcCliente(e.target.value)}
                      onFocus={() => setShowClienteDropdown(false)}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setModalCadastro(false); setErro('') }}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {modalEtapas && p && (
        <div
          className="modal fade show fc-modal-etapas"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fc-modal-etapas-titulo"
          onClick={() => setModalEtapas(false)}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="fc-modal-etapas-titulo">Configurar Etapas</h5>
                <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setModalEtapas(false)} />
              </div>
              <div className="modal-body">
                <div className="fc-etapa-secao">
                  <CamposEtapa
                    tituloPrazo="Parecer de Acesso (Prazo: 15 dias):"
                    status={p.parecer}
                    onStatus={(v) => atualizarCampo('parecer', v)}
                    protocolo={p.protoParecer}
                    onProtocolo={(v) => atualizarCampo('protoParecer', v)}
                    data={p.dataParecer}
                    onData={(v) => atualizarCampo('dataParecer', v)}
                  />
                </div>
                <div className="fc-etapa-secao">
                  <CamposEtapa
                    tituloPrazo="Solicitar Vistoria (Prazo: 5 dias úteis):"
                    status={p.statusVistoria}
                    onStatus={(v) => atualizarCampo('statusVistoria', v)}
                    protocolo={p.protoVistoria}
                    onProtocolo={(v) => atualizarCampo('protoVistoria', v)}
                    data={p.dataVistoria}
                    onData={(v) => atualizarCampo('dataVistoria', v)}
                  />
                </div>
                <div className="fc-etapa-secao">
                  <CamposEtapa
                    tituloPrazo="Aumento de Carga (Prazo: 5 dias úteis):"
                    status={p.statusCarga}
                    onStatus={(v) => atualizarCampo('statusCarga', v)}
                    protocolo={p.protoCarga}
                    onProtocolo={(v) => atualizarCampo('protoCarga', v)}
                    data={p.dataCarga}
                    onData={(v) => atualizarCampo('dataCarga', v)}
                  />
                </div>
                <div className="fc-etapa-secao">
                  <label className="form-label fw-semibold" htmlFor="fc-cliente-homologado">Cliente homologado</label>
                  <div className="form-check fc-homologado-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="fc-cliente-homologado"
                      checked={!!p.homologado}
                      onChange={(e) => atualizarHomologado(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="fc-cliente-homologado">
                      Marcar que o cliente foi homologado
                    </label>
                  </div>
                  {p.dataHomologado ? (
                    <p className="small text-muted mb-0 mt-2">
                      Data registrada: {formatarDataHoraHomolog(p.dataHomologado)}
                    </p>
                  ) : (
                    <p className="small text-muted mb-0 mt-2">
                      Ao marcar, gravamos <strong>homologado</strong> e <strong>data_homologado</strong> no cadastro do cliente. Ao desmarcar, a data é removida.
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalEtapas(false)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalWhatsapp && (
        <div
          className="modal fade show fc-modal-etapas"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-label={modalWhatsapp.tipo === TIPO_AVISO_FATURA ? 'Enviar aviso de fatura no WhatsApp' : 'Enviar aviso WhatsApp'}
          onClick={() => { if (!enviandoWhatsapp) setModalWhatsapp(null) }}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalWhatsapp.tipo === TIPO_AVISO_FATURA
                    ? 'Aviso de fatura — WhatsApp'
                    : 'Aviso de etapa — WhatsApp'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Fechar"
                  disabled={enviandoWhatsapp}
                  onClick={() => setModalWhatsapp(null)}
                />
              </div>
              <div className="modal-body">
                {(() => {
                  const st = rotuloStatusWhatsApp(modalWhatsapp.envio?.status)
                  const vars = modalWhatsapp.vars
                  const semTelefone = String(vars.telefone || '').replace(/\D/g, '').length < 10
                  const avisoFatura = modalWhatsapp.tipo === TIPO_AVISO_FATURA
                  return (
                    <>
                      {semTelefone && (
                        <div className="alert alert-warning py-2">
                          Este cliente não tem telefone cadastrado. Inclua o número em <strong>Cadastrar Clientes</strong> para enviar o aviso no WhatsApp.
                        </div>
                      )}
                      {erro && !semTelefone && <div className="alert alert-danger py-2">{erro}</div>}
                      <p className="mb-2">
                        <strong>{vars.nome_cliente}</strong>
                        {semTelefone
                          ? <span className="text-danger"> · sem telefone no cadastro</span>
                          : <span className="text-muted"> · {vars.telefone}</span>}
                      </p>
                      <div className={`alert py-2 fc-wa-status fc-wa-status--${st.classe}`}>
                        Status do aviso: <strong>{st.label}</strong>
                        {modalWhatsapp.envio?.created_at ? ` · ${formatarDataHoraHomolog(modalWhatsapp.envio.created_at)}` : ''}
                      </div>
                      {avisoFatura && (
                        <div className="alert alert-info py-2">
                          Disponível somente na etapa <strong>Aumento de Carga</strong>, para cobrar fatura vencida ou a vencer na Equatorial.
                        </div>
                      )}
                      <label className="form-label">
                        {avisoFatura ? 'Mensagem (template_pagar_fatura)' : 'Mensagem (variáveis da etapa atual)'}
                      </label>
                      <pre className="fc-wa-preview">{vars.preview}</pre>
                      {avisoFatura ? (
                        <ul className="small mb-3">
                          <li><strong>Cliente:</strong> {vars.nome_cliente}</li>
                        </ul>
                      ) : (
                        <ul className="small mb-3">
                          <li><strong>Status:</strong> {vars.status}</li>
                          <li><strong>Cliente:</strong> {vars.nome_cliente}</li>
                          <li><strong>Etapa:</strong> {vars.etapa}</li>
                          <li><strong>Prazo:</strong> {vars.prazo}</li>
                        </ul>
                      )}
                    </>
                  )
                })()}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" disabled={enviandoWhatsapp} onClick={() => setModalWhatsapp(null)}>
                  Fechar
                </button>
                <button
                  type="button"
                  className="btn fc-btn-enviar-aviso"
                  onClick={enviarAvisoHomologacao}
                  disabled={enviandoWhatsapp || String(modalWhatsapp.vars.telefone || '').replace(/\D/g, '').length < 10}
                >
                  {enviandoWhatsapp
                    ? 'Enviando...'
                    : (modalWhatsapp.tipo === TIPO_AVISO_FATURA ? 'Enviar aviso de fatura' : 'Enviar aviso da etapa')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!processoExcluir}
        title="Excluir processo"
        message={processoExcluir ? `Excluir todo o processo de ${processoExcluir.nome}? Esta ação não pode ser desfeita.` : ''}
        type="danger"
        confirmText={excluindo ? 'Excluindo...' : 'Excluir'}
        cancelText="Cancelar"
        onConfirm={excluirProcesso}
        onCancel={() => setProcessoExcluir(null)}
        zIndex={2000}
      />
    </div>
  )
}
